// @givmo/app-charity — ONBOARDING chrome (CPA-WEB10, D-CP-36).
//
// The frame around a caller who has no organization yet: brand, the signed-in identity, sign-out, and
// help. Deliberately NOT the SaaS shell — there is no sidebar, no org switcher, and no breadcrumb,
// because every destination those would offer (Dashboard, Grants, Payments, Profile, Settings) needs
// an organization this caller does not have. Framing the claim flow in an empty console makes the
// product look broken on the one screen that has to feel like a welcome.
//
// What it keeps from the shell is what stays true without an org: the wordmark, who you are signed in
// as, a way OUT (`menu-logout` — the same testid the shell uses, since exactly one chrome renders at
// a time and the affordance is the same one), and the in-app help page the claim flow's two
// "contact support" links point at.
//
// DATA CONTRACT (Playwright / RTL): `charity-onboarding-shell` (root), `charity-brand`,
// `charity-content`, `menu-logout`, `menu-help`, and the footer's `menu-privacy` — plus
// `menu-terms`, the ONE part of this chrome that is not unconditional (see the footer, D-CP-70).

import { Link, useRouterState } from '@tanstack/react-router';
import {
  Avatar,
  Button,
  GivmoWordmark,
  Icon,
  Skeleton,
  iconButtonVariants,
  testIdProp,
} from '@givmo/design-system';
import { LifeBuoy, LogOut } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/auth-context';
import { MAIN_CONTENT_ID, SkipToContent } from './skip-to-content';
import { useSignOut } from '../auth/use-sign-out';
import { TERMS_URL, PRIVACY_URL } from '../lib/constants';

export interface OnboardingShellProps {
  children: ReactNode;
}

/**
 * Whether the matched route declares that this chrome's footer must NOT offer the Platform Terms
 * (`staticData.hidesOnboardingFooterTerms`, declared in `router/routes.tsx` — D-CP-70).
 *
 * Read off the resolved MATCHES, never off the pathname: the address of the route that declares it
 * is the route tree's to spell, and a path shape matched here would be a second spelling of it —
 * the reason `signedOutDoorFor` reads the same way.
 *
 * ⚠ It differs from `signedOutDoorFor` in ONE way, deliberately: that one walks leaf → root and stops
 * at the innermost declaration, because a door is a VALUE a leaf must be able to override. This is a
 * PREDICATE — the field's type is `?: true`, so there is no `false` to override with — and any
 * declaration in the matched chain hides the link, a layout's included. Pinned in
 * `router/onboarding-footer-terms.test.tsx`, which is what "exported" is for.
 */
export function footerHidesTermsLink(state: {
  matches: ReadonlyArray<{ staticData?: { hidesOnboardingFooterTerms?: true } }>;
}): boolean {
  return state.matches.some((match) => match.staticData?.hidesOnboardingFooterTerms === true);
}

export function OnboardingShell({ children }: OnboardingShellProps) {
  const { user } = useAuth();
  const { signOut, isSigningOut } = useSignOut();
  const hidesTermsLink = useRouterState({ select: footerHidesTermsLink });

  const accountName = user?.displayName?.trim() || null;
  const accountEmail = user?.email?.trim() || null;
  const accountPrimary = accountName ?? accountEmail;

  return (
    <div
      className="flex min-h-screen flex-col bg-surface-sunken"
      {...testIdProp('charity-onboarding-shell')}
    >
      <SkipToContent />
      <header className="flex h-[56px] shrink-0 items-center justify-between gap-16 border-b border-border-subtle bg-surface px-24">
        {/* The brand is NOT a link here: the console is the one place this caller cannot go. */}
        <span className="flex items-center gap-8" {...testIdProp('charity-brand')}>
          <GivmoWordmark className="h-[22px] w-auto text-brand-500" />
        </span>

        <div className="flex items-center gap-10">
          <Link
            to="/support"
            aria-label="Help and support"
            className={iconButtonVariants({ tone: 'ghost', size: 'md' })}
            {...testIdProp('menu-help')}
          >
            <Icon icon={LifeBuoy} size={18} aria-hidden />
          </Link>

          {accountPrimary !== null ? (
            <span className="flex min-w-0 items-center gap-8">
              <Avatar name={accountPrimary} src={user?.photoURL ?? undefined} size="sm" />
              <span className="hidden min-w-0 truncate font-sans text-s13 font-w500 tracking-givmo text-text-secondary sm:block">
                {accountPrimary}
              </span>
            </span>
          ) : (
            <Skeleton className="size-32 rounded-full" aria-label="Loading account" />
          )}

          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={isSigningOut}
            aria-busy={isSigningOut || undefined}
            leadingIcon={<Icon icon={LogOut} size={14} aria-hidden />}
            onClick={signOut}
            testId="menu-logout"
          >
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </Button>
        </div>
      </header>

      <main
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className="min-w-0 flex-1 outline-none"
        {...testIdProp('charity-content')}
      >
        {children}
      </main>

      {/* ★ THE TERMS LINK IS DROPPED WHERE THE SCREEN ITSELF IS THE TERMS (D-CP-70, Jonathan
          2026-09-12: "Hide the Terms link in the bottom left altogether only on the claims wizard
          because all it can do is create confusion since we are showing them the actual charity
          terms text in the wizard"). This word opens the PLATFORM Terms; the wizard's own ceremony
          reads the CHARITY Terms in flow, so on that one route the footer would put two different
          documents behind one word on a screen that ends in a recorded §318(a) consent. The
          separator goes with it — a leading `·` before Privacy is a link that failed to render —
          and nothing else about the row changes, so every other pre-claim screen's footer is
          untouched. WHICH route is the route's to declare, not this file's to match. */}
      <footer className="flex shrink-0 items-center justify-center gap-12 px-24 py-16">
        {!hidesTermsLink && (
          <>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary outline-none transition-colors hover:text-text-strong focus-visible:text-text-strong motion-reduce:transition-none"
              {...testIdProp('menu-terms')}
            >
              Terms
            </a>
            <span aria-hidden className="text-text-placeholder">
              ·
            </span>
          </>
        )}
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noreferrer"
          className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary outline-none transition-colors hover:text-text-strong focus-visible:text-text-strong motion-reduce:transition-none"
          {...testIdProp('menu-privacy')}
        >
          Privacy
        </a>
      </footer>
    </div>
  );
}
